import React, { useMemo, useCallback } from 'react';
import get from 'lodash.get';
import { useSelector, useDispatch } from 'react-redux';

import DrawerBatchTitle from './drawer-batch-title';
import { getBatchListsFromState } from '../../containers/batches/get-batch-collections-from-state';
import { useOrdersGridFilters } from '../../hooks/filters/use-orders-grid-filters';
import { useTopLevelOrdersViewId } from '../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { CANCELLED_ID } from '../../pages/orders-page/orders-page-view-constants';
import { addFulfillmentPlansOrSalesOrderToBatchRequest, createBatchWithFulfillmentPlans } from '../../store/batches/batches.actions';
import { loadMoreActiveBatches } from '../../store/batches/nav/batches.nav.actions';
import Global from '../../store/global-selectors';

import AddToBatchDropdown from './index';

/**
 * @typedef {import('ui-components/components/dropdown/dropdown-types.d.ts').DropdownMenuPositions} DropdownMenuPositions
 */

/**
 * @param {object} props
 * @param {string} props.dropdownTogglerClassname
 * @param {DropdownMenuPositions} props.menuPosition
 * @returns {JSX.Element}
 */
const OrdersDrawerAddToBatchDropdownContainer = ({ dropdownTogglerClassname, menuPosition }) => {
  const dispatch = useDispatch();

  const areAllSelected = useSelector(Global.selectSelectedOrdersAreAllOrdersSelected);
  const selectedOrderIds = useSelector(Global.selectSelectedOrdersIds);
  const ordersGridFilters = useOrdersGridFilters();
  const isCancelled = useTopLevelOrdersViewId() === CANCELLED_ID;
  const userId = useSelector(Global.selectUserCurrentUserId);
  const selectedFulfillmentPlans = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds: selectedOrderIds,
    }),
  );
  const pageSize = useSelector(Global.selectBatchesNavPageSize);
  const totalBatchesCount = useSelector(Global.selectBatchesNavTotalBatchesCount);
  const { myBatches, otherBatches } = useSelector(getBatchListsFromState);

  const filters = useMemo(() => (areAllSelected ? ordersGridFilters : undefined), [areAllSelected, ordersGridFilters]);
  const selectedFulfillmentPlanSetIds = useMemo(
    () => [...new Set(selectedFulfillmentPlans.map((fulfillmentPlan) => `${fulfillmentPlan.fulfillmentPlanSetId}`))].filter((id) => !!id),
    [selectedFulfillmentPlans],
  );
  const selectedBatchNames = useMemo(
    () =>
      selectedFulfillmentPlanSetIds.map((id) => {
        const matchingBatch = [...myBatches, ...otherBatches].find((batch) => batch.fulfillmentPlanSetId === id);
        return get(matchingBatch, 'name', '');
      }),
    [selectedFulfillmentPlanSetIds, myBatches, otherBatches],
  );
  const shouldShowLoadMoreButton = pageSize < totalBatchesCount;

  const addIdsToBatch = useCallback(
    (fulfillmentPlanSetId) => dispatch(addFulfillmentPlansOrSalesOrderToBatchRequest(fulfillmentPlanSetId, selectedOrderIds, filters)),
    [dispatch, filters, selectedOrderIds],
  );

  const createBatchWithIds = useCallback(
    () => dispatch(createBatchWithFulfillmentPlans(undefined, userId, selectedOrderIds, false, filters)),
    [dispatch, filters, selectedOrderIds, userId],
  );

  const loadMore = useCallback(() => dispatch(loadMoreActiveBatches()), [dispatch]);

  return (
    <AddToBatchDropdown
      addIdsToBatch={addIdsToBatch}
      createBatchWithIds={createBatchWithIds}
      hide={isCancelled}
      ids={selectedOrderIds}
      loadMore={loadMore}
      menuPosition={menuPosition}
      myBatches={myBatches}
      otherBatches={otherBatches}
      shouldShowLoadMoreButton={shouldShowLoadMoreButton}
      togglerClassName={dropdownTogglerClassname}
      togglerTheme='link'
      userId={userId}
    >
      <DrawerBatchTitle batchNames={selectedBatchNames} />
    </AddToBatchDropdown>
  );
};

export default OrdersDrawerAddToBatchDropdownContainer;
